import { Controller } from "@hotwired/stimulus"

// Load panel shared by the save tools (games/tools/_save_loader.html.erb). Reads the chosen or
// dropped file in the browser and dispatches `save-loader:loaded` with { file, content };
// parsing stays with the game's controller, which reaches back through its save-loader outlet
// (showError, showFile, ready, …).
export default class extends Controller {
  static targets = ["dropzone", "input", "status", "error", "fileSummary", "fileName", "fileMeta", "emptySave", "emptyButton"]
  // readAs: "text", "arrayBuffer", or "none" (content is null; the game reads the File itself).
  static values = { readAs: { type: String, default: "text" } }

  connect() {
    this.defaultAccept = this.inputTarget.accept
    this.boundDragEnter = (e) => this.onDragEnter(e)
    this.boundDragLeave = (e) => this.onDragLeave(e)
    this.boundDrop = (e) => this.onDrop(e)
    this.dropzoneTarget.addEventListener("dragenter", this.boundDragEnter)
    this.dropzoneTarget.addEventListener("dragover", this.boundDragEnter)
    this.dropzoneTarget.addEventListener("dragleave", this.boundDragLeave)
    this.dropzoneTarget.addEventListener("drop", this.boundDrop)
  }

  disconnect() {
    this.dropzoneTarget.removeEventListener("dragenter", this.boundDragEnter)
    this.dropzoneTarget.removeEventListener("dragover", this.boundDragEnter)
    this.dropzoneTarget.removeEventListener("dragleave", this.boundDragLeave)
    this.dropzoneTarget.removeEventListener("drop", this.boundDrop)
  }

  // --- Called by the game's controller ----------------------------------------

  /** Game data has loaded; the file input can be used. */
  ready() {
    this.inputTarget.disabled = false
    if (this.hasEmptyButtonTarget) this.emptyButtonTarget.disabled = false
    if (this.hasStatusTarget) this.statusTarget.hidden = true
  }

  showError(message) {
    this.element.hidden = false
    this.errorTarget.textContent = message
    this.errorTarget.hidden = false
  }

  clearError() {
    this.errorTarget.hidden = true
    this.errorTarget.textContent = ""
  }

  /** Swap the dropzone for a bar naming the loaded file, with "Load a different file". */
  showFile(name, meta = "") {
    this.fileNameTarget.textContent = name
    this.fileMetaTarget.textContent = meta
    this.fileSummaryTarget.hidden = false
    this.dropzoneTarget.hidden = true
    if (this.hasEmptySaveTarget) this.emptySaveTarget.hidden = true
  }

  /**
   * Open the file picker again (e.g. a "Load another save" button in the report). `accept` narrows
   * the picker's file types for this one opening.
   */
  chooseAnother(accept = null) {
    this.inputTarget.accept = accept || this.defaultAccept
    this.inputTarget.value = ""
    this.inputTarget.click()
  }

  /** Back to an empty dropzone; the game's controller hears `save-loader:reset`. */
  reset() {
    this.inputTarget.value = ""
    if (this.hasFileSummaryTarget) this.fileSummaryTarget.hidden = true
    this.dropzoneTarget.hidden = false
    if (this.hasEmptySaveTarget) this.emptySaveTarget.hidden = false
    this.clearError()
    this.dispatch("reset")
  }

  /** Browse with no save: the game's controller hears `save-loader:empty` and shows a fresh one. */
  chooseEmpty() {
    this.clearError()
    this.dispatch("empty")
  }

  // --- File input and drag-drop ---------------------------------------------

  choose() {
    const file = this.inputTarget.files?.[0]
    if (file) this.read(file)
  }

  onDragEnter(event) {
    event.preventDefault()
    this.dropzoneTarget.classList.add("is-dragover")
  }

  onDragLeave(event) {
    event.preventDefault()
    this.dropzoneTarget.classList.remove("is-dragover")
  }

  onDrop(event) {
    event.preventDefault()
    this.dropzoneTarget.classList.remove("is-dragover")
    const file = event.dataTransfer?.files?.[0]
    if (file && !this.inputTarget.disabled) this.read(file)
  }

  async read(file) {
    this.clearError()
    let content = null
    try {
      if (this.readAsValue === "text") content = await file.text()
      else if (this.readAsValue === "arrayBuffer") content = await file.arrayBuffer()
    } catch {
      this.showError("Couldn't read that file. Try choosing it again.")
      return
    }
    this.dispatch("loaded", { detail: { file, content } })
  }
}
